import { CredentialType } from '../shared';
import type { CredentialFieldConfig } from './types';

const FILE_PATH_WARNING = `⚠️ IMPORTANT: The file path you specify determines where your uploaded file is stored.

Using a path that already exists will:
• SILENTLY OVERWRITE the existing file
• BREAK any credential that was using that file
• PERMANENTLY DESTROY the original file contents

Always use a unique, namespaced path:
✓ Good: "my-funder-name/id_rsa"
✓ Good: "acme-corp-sftp/known_hosts"
✗ Bad: "id_rsa" (no namespace, likely to collide)
✗ Bad: "/id_rsa" (invalid - starts with /)`;

export const SFTP_FIELD_CONFIGS: CredentialFieldConfig[] = [
  {
    key: 'username',
    label: 'Username',
    type: 'text',
    required: false,
    helpText: 'SSH username for credential. Required if using password auth.',
    helpSeverity: 'info',
    placeholder: 'Enter SSH username',
  },
  {
    key: 'password',
    label: 'Password',
    type: 'secret',
    required: false,
    helpText: 'SSH password for credential. Use with username, or use private key instead.',
    helpSeverity: 'info',
    placeholder: 'Enter SSH password',
  },
  {
    key: 'keyFilePath',
    label: 'Private Key Path',
    type: 'filePath',
    required: false,
    helpText: FILE_PATH_WARNING,
    helpSeverity: 'warning',
    placeholder: 'e.g., my-entity/id_rsa',
    fileUploadKey: 'privateKey',
  },
  {
    key: 'keyPassphrase',
    label: 'Key Passphrase',
    type: 'secret',
    required: false,
    helpText: 'Passphrase to decrypt the private key, if encrypted. Leave empty if key is not encrypted.',
    helpSeverity: 'info',
    placeholder: 'Enter key passphrase (if encrypted)',
  },
  {
    key: 'knownHostsFile',
    label: 'Known Hosts Path',
    type: 'filePath',
    required: true,
    helpText: FILE_PATH_WARNING,
    helpSeverity: 'warning',
    placeholder: 'e.g., my-entity/known_hosts',
    fileUploadKey: 'knownHosts',
  },
];

export const AWS_FIELD_CONFIGS: CredentialFieldConfig[] = [
  {
    key: 'accessKeyId',
    label: 'Access Key ID',
    type: 'text',
    required: false,
    helpText: 'AWS Access Key ID for IAM user credential.',
    helpSeverity: 'info',
    placeholder: 'AKIA...',
  },
  {
    key: 'secretAccessKey',
    label: 'Secret Access Key',
    type: 'secret',
    required: false,
    helpText: 'AWS Secret Access Key for IAM user credential. Keep this secure.',
    helpSeverity: 'info',
    placeholder: 'Enter secret access key',
  },
  {
    key: 'roleArn',
    label: 'Role ARN',
    type: 'text',
    required: false,
    helpText: 'ARN of an IAM role to assume. Use this instead of access keys for cross-account access.',
    helpSeverity: 'info',
    placeholder: 'arn:aws:iam::123456789012:role/MyRole',
  },
  {
    key: 'region',
    label: 'Region',
    type: 'text',
    required: false,
    helpText: 'AWS region where your S3 bucket is located (e.g., us-east-1).',
    helpSeverity: 'info',
    placeholder: 'us-east-1',
  },
];

export const GCP_FIELD_CONFIGS: CredentialFieldConfig[] = [
  {
    key: 'credentialFilePath',
    label: 'Service Account JSON Path',
    type: 'filePath',
    required: true,
    helpText: FILE_PATH_WARNING,
    helpSeverity: 'warning',
    placeholder: 'e.g., my-entity/gcp-sa.json',
    fileUploadKey: 'serviceAccountJson',
  },
];

export const AZURE_FIELD_CONFIGS: CredentialFieldConfig[] = [
  {
    key: 'accountName',
    label: 'Account Name',
    type: 'text',
    required: false,
    helpText: 'Azure Storage account name.',
    helpSeverity: 'info',
    placeholder: 'Enter storage account name',
  },
  {
    key: 'accountKey',
    label: 'Account Key',
    type: 'secret',
    required: false,
    helpText: 'Azure Storage account access key.',
    helpSeverity: 'info',
    placeholder: 'Enter account key',
  },
  {
    key: 'tenantId',
    label: 'Tenant ID',
    type: 'text',
    required: false,
    helpText: 'Azure AD tenant ID for service principal auth.',
    helpSeverity: 'info',
    placeholder: 'Enter tenant ID (GUID)',
  },
  {
    key: 'clientId',
    label: 'Client ID',
    type: 'text',
    required: false,
    helpText: 'Azure AD application (client) ID.',
    helpSeverity: 'info',
    placeholder: 'Enter client ID (GUID)',
  },
  {
    key: 'clientSecret',
    label: 'Client Secret',
    type: 'secret',
    required: false,
    helpText: 'Azure AD client secret for service principal auth.',
    helpSeverity: 'info',
    placeholder: 'Enter client secret',
  },
];

export const getFieldConfigsForCredentialType = (type: CredentialType): CredentialFieldConfig[] => {
  switch (type) {
    case CredentialType.CREDENTIAL_TYPE_SSH:
      return SFTP_FIELD_CONFIGS;
    case CredentialType.CREDENTIAL_TYPE_AWS:
      return AWS_FIELD_CONFIGS;
    case CredentialType.CREDENTIAL_TYPE_GCP_SERVICE_ACCOUNT:
      return GCP_FIELD_CONFIGS;
    case CredentialType.CREDENTIAL_TYPE_AZURE_SERVICE_PRINCIPAL:
      return AZURE_FIELD_CONFIGS;
    default:
      return [];
  }
};

export const getFilePathFields = (type: CredentialType): CredentialFieldConfig[] => {
  return getFieldConfigsForCredentialType(type).filter((field) => field.type === 'filePath');
};
